import React, { useContext } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import { Alert, IconButton, Stack, Typography } from '@mui/material';
import { Close, Done } from '@mui/icons-material';
import { PickYourPlanIcon } from '../../../../vectors/index';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { LoadingButton } from '@mui/lab';
import { PLANS } from '@utils/pricing';
import { authContext } from '@staffedupusa/shared-components';
import { useUpdatePlan } from '@features/payments/hooks/useUpdatePlan';

interface SelectPlanForExpiredProps {
  open: boolean;
  hasPaymentMethod: boolean;
  currentPlan: 1 | 2 | 4 | 0 | 999;
  handleClose: () => void;
  companyName: string | null;
  companyId: number | null;
}

const SelectPlanForExpired: React.FC<SelectPlanForExpiredProps> = ({
  open,
  handleClose,
  companyName,
  companyId,
  hasPaymentMethod,
  currentPlan,
}) => {
  const navigate = useNavigate();
  const { authData } = useContext(authContext);
  const billingCycle = authData?.user?.billing_cycle;
  const [selectedPlan, setSelectedPlan] = React.useState<1 | 2 | 3 | 4>(
    currentPlan !== 0 && currentPlan !== 999 ? currentPlan : 4,
  );
  const [errorText, setErrorText] = React.useState<string>('');

  const { updatePaymentPlan, isUpdatingPaymentPlan } = useUpdatePlan({
    setErrorText,
  });

  const handlePlanChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedPlan(
      Number((event.target as HTMLInputElement).value) as 1 | 2 | 3,
    );
  };

  const handleSubmit = () => {
    if (!hasPaymentMethod) {
      toast.error(
        'Please add a payment method to your account before upgrading your plan',
      );
      navigate('#Payment-Info');
      return;
    }
    const options = {
      onSuccess: (data: { status: number; message: string }) => {
        if (data.status == 200) {
          setErrorText('');
          toast.success(data.message);
          handleClose();
        }
      },
    };
    if (companyId)
      updatePaymentPlan(
        {
          billing_cycle: billingCycle ?? undefined,
          upgrade_plan: false,
          downgrade_plan: false,
          switch: false,
          plan_ids: [
            {
              company_id: companyId,
              plan_id: currentPlan,
              new_plan_id: selectedPlan,
            },
          ],
        },
        options,
      );
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        <IconButton
          sx={{ position: 'absolute', top: 5, right: 5 }}
          onClick={handleClose}
        >
          <Close />
        </IconButton>
        <br />
        <PickYourPlanIcon />
        <Typography variant="h4" mt={4}>
          Select Plan for <br /> <b>{companyName}</b>{' '}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        {errorText && <Alert severity="error">{errorText}</Alert>}
        <FormControl component="fieldset">
          <FormLabel component="legend">Select plan</FormLabel>
          <RadioGroup
            aria-label="plan"
            name="plan"
            value={selectedPlan}
            onChange={handlePlanChange}
            row
          >
            {/* <FormControlLabel
              value={4}
              control={<Radio />}
              label={PLANS.STARTER.NAME}
            /> */}
            <FormControlLabel
              value={1}
              control={<Radio />}
              label={PLANS.SMARTER.NAME}
            />
            <FormControlLabel
              value={2}
              control={<Radio />}
              label={PLANS.PRO.NAME}
            />
          </RadioGroup>
        </FormControl>
        <Stack sx={{ with: '100%' }} spacing={2} mb={2} mt={2}>
          <LoadingButton
            loading={isUpdatingPaymentPlan}
            variant="contained"
            startIcon={<Done />}
            onClick={handleSubmit}
            color="primary"
            disabled={selectedPlan === 4}
          >
            Confirm
          </LoadingButton>
          <Button
            disabled={isUpdatingPaymentPlan}
            startIcon={<Close />}
            variant="outlined"
            color="secondary"
            onClick={handleClose}
          >
            Cancel
          </Button>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default SelectPlanForExpired;
